/**
 * Admin V3 — Tarification (Pricing) — orchestrator
 *
 * Mounted at: /admin-v3/pricing (see frontend/src/pages/AdminV3.js)
 *
 * Owner-authorized Phase 1 of the Admin Consolidation & Tarification
 * Migration plan. Surfaces the Stage C Schedule A configuration through
 * the AdminV3 shell, replacing the prior ModulePlaceholder.
 *
 * SCOPE (Phase 1 binding — six owner corrections):
 *   1. Legacy config (7 fields) is READ-ONLY and PASS-THROUGH.
 *      The PUT body re-uses the values returned by the latest GET
 *      verbatim; never hardcoded.
 *   2. Business/VAN multipliers are displayed as persisted-but-dormant
 *      and read-only. They are preserved from the GET response and
 *      included verbatim in the PUT body. NO editing controls.
 *   3. Priority is fully excluded. No UI row exists. Client-side
 *      validation rejects any 'priority' key case-insensitively.
 *   4. Fixed routes: existing B-A routes may be edited in place ONLY.
 *      Add/Delete are deferred to a future phase (buttons disabled).
 *   5. Legacy admin files remain UNTOUCHED.
 *   6. Backend is strictly out of scope.
 *
 * RBAC posture (HIDE-RBAC, defense-in-depth):
 *   - Read     gate: pricing:config:read   → page renders read-only view
 *   - Update   gate: pricing:config:update → enables editing controls
 *     The backend `@rbac_permission` is authoritative; this is ergonomic.
 *
 * Submission flow:
 *   1. Operator edits Schedule A fields
 *   2. Operator clicks "Vérifier et confirmer…"
 *   3. validateScheduleA runs (Priority rejected case-insensitively here)
 *   4. diffScheduleA renders the modal diff
 *   5. Operator acknowledges + types CONFIRMER
 *   6. PUT /pricing/config sent with { config: <legacy from latest GET>,
 *      schedule_a: <edited> }
 *   7. On success: refetch the GET, dismiss modal, show success toast.
 *   8. On 5-min cache propagation: surface the TTL hint in the toast.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Settings, AlertTriangle, RefreshCw, Lock, CheckCircle } from 'lucide-react';
import { SectionCard, EmptyState, cn } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import { useToast } from '../../shared/hooks/useToast';

import BandsEditor from './BandsEditor';
import ClassMultipliersPanel from './ClassMultipliersPanel';
import FixedRoutesEditor from './FixedRoutesEditor';
import LegacyConfigPanel from './LegacyConfigPanel';
import ConfirmationModal from './ConfirmationModal';
import validateScheduleA, { hasPriorityKey } from './utils/validateScheduleA';
import diffScheduleA from './utils/diffScheduleA';

// =============================================================================
// CONSTANTS
// =============================================================================

const READ_PERMISSION   = 'pricing:config:read';
const UPDATE_PERMISSION = 'pricing:config:update';

// The 7 legacy fields per backend PricingConfig schema. These are pass-through
// only — we never hardcode their values. The PUT body sources them from the
// latest GET response.
const LEGACY_KEYS = [
  'base_fare',
  'price_per_km',
  'price_per_minute',
  'minimum_fare',
  'night_multiplier',
  'weekend_multiplier',
  'commission_rate',
];

const labels = {
  pageTitle:           'Tarification',
  subtitle:            'Schedule A — bandes, multiplicateurs par classe et tarifs forfaitaires Schedule B-A',
  loading:             'Chargement de la configuration tarifaire…',
  accessDenied:        'Accès restreint',
  accessDeniedDesc:    "Vous n'avez pas la permission pricing:config:read requise pour consulter ce module.",
  errorTitle:          'Erreur de chargement',
  retry:               'Réessayer',
  ttlBanner:           'Le moteur de tarification met en cache la configuration pendant 5 minutes (Gate G2). Les modifications confirmées peuvent prendre jusqu\'à 5 minutes pour se propager à tous les devis.',
  noScheduleA:         'Schedule A n\'est pas encore initialisé dans la configuration. Veuillez exécuter SC-OPS-1 avant d\'utiliser ce module.',
  submitDisabledHint:  'Permission pricing:config:update requise pour modifier la configuration.',
  submit:              'Vérifier et confirmer…',
  successToast:        'Configuration tarifaire mise à jour. Propagation dans les 5 minutes (Gate G2).',
  errorTitleModal:     'Erreur lors de la mise à jour',
  validationFailed:    'Validation locale échouée — corrigez les erreurs marquées en rouge avant de soumettre.',
  priorityRejected:    "La clé 'priority' est interdite (Gate A.1). Veuillez la retirer avant de soumettre.",
};

// =============================================================================
// HELPERS
// =============================================================================

const isAuthError = (error) => {
  if (!error) return false;
  const msg = String(error).toLowerCase();
  return msg.includes('401') || msg.includes('403')
      || msg.includes('forbidden') || msg.includes('unauthorized')
      || msg.includes('access denied') || msg.includes('permission');
};

// Deep clone for the editable Schedule A copy. The legacy config is never
// cloned — we keep the GET reference and pass it through unchanged.
const cloneScheduleA = (sa) => {
  if (!sa) return null;
  return {
    bands: Array.isArray(sa.bands) ? sa.bands.map((b) => ({ ...b })) : [],
    class_multipliers: sa.class_multipliers ? { ...sa.class_multipliers } : {},
    fixed_routes: Array.isArray(sa.fixed_routes)
      ? sa.fixed_routes.map((r) => ({
          ...r,
          pickup_keywords: Array.isArray(r.pickup_keywords) ? [...r.pickup_keywords] : [],
          dropoff_keywords: Array.isArray(r.dropoff_keywords) ? [...r.dropoff_keywords] : [],
          service_types: Array.isArray(r.service_types) ? [...r.service_types] : [],
        }))
      : [],
  };
};

// Strip any priority key from class_multipliers (defense-in-depth before PUT).
// Mirrors the backend validator's case-insensitive rejection.
const stripPriorityKeys = (cm) => {
  if (!cm || typeof cm !== 'object') return cm;
  const out = {};
  Object.entries(cm).forEach(([k, v]) => {
    if (typeof k === 'string' && k.toLowerCase() === 'priority') return;
    out[k] = v;
  });
  return out;
};

// Pick only the 7 legacy fields from a config object — anything else is
// ignored to avoid sending unsupported keys to the backend.
const pickLegacy = (config) => {
  const out = {};
  if (!config || typeof config !== 'object') return out;
  LEGACY_KEYS.forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(config, k)) {
      out[k] = config[k];
    }
  });
  return out;
};

const normalizeErrorString = (e) => {
  if (!e) return '';
  if (typeof e === 'string') return e;
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return status ? `${status} — ${detail}` : detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
  }
  if (detail && typeof detail === 'object') {
    try { return JSON.stringify(detail); } catch (_) { return String(detail); }
  }
  return e?.message || String(e);
};

// =============================================================================
// MAIN
// =============================================================================

const PricingConfigPage = () => {
  // Permissions
  const { can, isResolved } = usePermissions();
  const canRead   = can(READ_PERMISSION);
  const canUpdate = can(UPDATE_PERMISSION);

  // Query (GET) + Mutation (PUT)
  const query    = useAdminQuery('/pricing/config');
  const mutation = useAdminMutation('/pricing/config', 'put');
  const { showSuccess, showError } = useToast();

  // ------------------------- Working copy of Schedule A -------------------------
  // The legacy 7 fields are kept untouched and sourced from `query.data` at
  // submit time (never duplicated into local state, never hardcoded).
  const [draft, setDraft] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalError, setModalError] = useState('');

  // Hydrate draft when GET response arrives or refetch returns.
  useEffect(() => {
    if (!query.data) return;
    const sa = query.data?.schedule_a;
    setDraft(cloneScheduleA(sa));
    setValidationErrors({});
  }, [query.data]);

  // ------------------------- Loading / error gates -------------------------
  const isLoading    = query.loading && !query.data;
  const accessDenied = isAuthError(query.error);
  const hardError    = !accessDenied && query.error;

  // ------------------------- Edit handlers (controlled) -------------------------

  const onBandsChange = useCallback((nextBands) => {
    setDraft((prev) => (prev ? { ...prev, bands: nextBands } : prev));
  }, []);

  const onClassMultipliersChange = useCallback((nextCm) => {
    setDraft((prev) => (prev ? { ...prev, class_multipliers: nextCm } : prev));
  }, []);

  const onFixedRoutesChange = useCallback((nextRoutes) => {
    setDraft((prev) => (prev ? { ...prev, fixed_routes: nextRoutes } : prev));
  }, []);

  // ------------------------- Diff (for modal) -------------------------

  const diffEntries = useMemo(() => {
    const current = query.data?.schedule_a || null;
    return diffScheduleA(current, draft);
  }, [query.data, draft]);

  // ------------------------- Validation + open modal -------------------------

  const handleOpenModal = useCallback(() => {
    if (!draft || !canUpdate) return;

    // Defense-in-depth Priority rejection BEFORE the generic validator.
    if (hasPriorityKey(draft.class_multipliers)) {
      setValidationErrors({
        'class_multipliers.priority': labels.priorityRejected,
      });
      return;
    }

    const { ok, errors } = validateScheduleA(draft);
    setValidationErrors(errors);
    if (!ok) return;

    // No diff — nothing to confirm.
    if (!diffEntries.length) return;

    setModalError('');
    setModalOpen(true);
  }, [draft, canUpdate, diffEntries.length]);

  // ------------------------- Submit (PUT) -------------------------

  const handleConfirm = useCallback(async () => {
    if (!draft) return;
    setModalError('');

    // Source legacy fields from the LATEST GET response (never hardcoded).
    // This satisfies owner correction #1: "Before any PUT, use latest GET
    // as source of truth. No stale hardcoded legacy values."
    const latestConfig = query.data?.config || query.data;
    const legacy = pickLegacy(latestConfig);

    // Strip any sneaked-in Priority key (defense-in-depth).
    const sanitizedCm = stripPriorityKeys(draft.class_multipliers);

    const body = {
      // Legacy 7 fields — pass-through, unmodified.
      ...legacy,
      // Schedule A — the operator's edits.
      schedule_a: {
        bands: draft.bands,
        class_multipliers: sanitizedCm,
        fixed_routes: draft.fixed_routes,
      },
    };

    const result = await mutation.mutate(body);
    if (!result) {
      const message = normalizeErrorString(mutation.error);
      setModalError(message || labels.errorTitleModal);
      if (showError) showError(message || labels.errorTitleModal);
      return;
    }

    setModalOpen(false);
    if (showSuccess) showSuccess(labels.successToast);

    // Refetch to refresh `query.data` and re-hydrate `draft` from server truth.
    if (query.refetch) query.refetch();
  }, [draft, query, mutation, showSuccess, showError]);

  const handleCloseModal = useCallback(() => {
    if (mutation.loading) return;
    setModalOpen(false);
    setModalError('');
  }, [mutation.loading]);

  // ------------------------- Render guards -------------------------

  // Render-time read gate (HIDE-RBAC). When unresolved, render the read-only
  // view exactly as before — backend remains authoritative.
  const readGateOpen = !isResolved || canRead;

  if (!readGateOpen) {
    return (
      <div className="space-y-5" data-testid="adminv3-pricing-page">
        <SectionCard title={labels.pageTitle} icon={Settings} iconClass="bg-amber-100 text-amber-600">
          <EmptyState icon={Lock} title={labels.accessDenied} desc={labels.accessDeniedDesc} />
        </SectionCard>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="space-y-5" data-testid="adminv3-pricing-page">
        <SectionCard title={labels.pageTitle} icon={Settings} iconClass="bg-amber-100 text-amber-600">
          <EmptyState icon={Lock} title={labels.accessDenied} desc={labels.accessDeniedDesc} />
        </SectionCard>
      </div>
    );
  }

  if (hardError) {
    return (
      <div className="space-y-5" data-testid="adminv3-pricing-page">
        <SectionCard title={labels.pageTitle} icon={Settings} iconClass="bg-amber-100 text-amber-600">
          <EmptyState
            icon={AlertTriangle}
            title={labels.errorTitle}
            desc={String(query.error)}
            action={
              <button
                type="button"
                onClick={() => query.refetch && query.refetch()}
                className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-1.5 text-[13px] font-semibold text-white hover:bg-emerald-700"
                data-testid="adminv3-pricing-retry"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {labels.retry}
              </button>
            }
          />
        </SectionCard>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-5" data-testid="adminv3-pricing-page">
        <SectionCard title={labels.pageTitle} icon={Settings} iconClass="bg-amber-100 text-amber-600">
          <div
            className="py-14 text-center text-sm text-slate-400"
            data-testid="adminv3-pricing-loading"
          >
            {labels.loading}
          </div>
        </SectionCard>
      </div>
    );
  }

  const legacy = query.data?.config || query.data || {};
  const scheduleA = draft || query.data?.schedule_a;

  if (!scheduleA) {
    return (
      <div className="space-y-5" data-testid="adminv3-pricing-page">
        <SectionCard title={labels.pageTitle} icon={Settings} iconClass="bg-amber-100 text-amber-600">
          <EmptyState
            icon={AlertTriangle}
            title="Schedule A absent"
            desc={labels.noScheduleA}
          />
        </SectionCard>
        <LegacyConfigPanel legacy={legacy} />
      </div>
    );
  }

  const submitDisabled = !canUpdate || mutation.loading || diffEntries.length === 0;

  // ------------------------- Render -------------------------

  return (
    <div className="space-y-5" data-testid="adminv3-pricing-page">
      {/* Header */}
      <SectionCard title={labels.pageTitle} icon={Settings} iconClass="bg-amber-100 text-amber-600">
        <div className="space-y-2">
          <p className="text-[13px] text-slate-600">{labels.subtitle}</p>
          <div className="flex items-start gap-2 rounded-xl bg-slate-50 px-3 py-2 text-[11.5px] italic text-slate-600">
            <CheckCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-600" />
            <span>{labels.ttlBanner}</span>
          </div>
        </div>
      </SectionCard>

      {/* Bands */}
      <BandsEditor
        bands={scheduleA.bands}
        onChange={onBandsChange}
        errors={validationErrors}
        canEdit={canUpdate}
      />

      {/* Class multipliers (editable: standard/corporate/partner; read-only: business/van) */}
      <ClassMultipliersPanel
        classMultipliers={scheduleA.class_multipliers}
        onChange={onClassMultipliersChange}
        errors={validationErrors}
        canEdit={canUpdate}
      />

      {/* Fixed routes (edit-in-place only) */}
      <FixedRoutesEditor
        routes={scheduleA.fixed_routes}
        onChange={onFixedRoutesChange}
        errors={validationErrors}
        canEdit={canUpdate}
      />

      {/* Legacy decorative config */}
      <LegacyConfigPanel legacy={legacy} />

      {/* Submit row */}
      <div className="sticky bottom-3 z-10 flex items-center justify-end gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_8px_30px_rgba(15,23,42,0.06)]">
        {!canUpdate && (
          <span className="text-[12px] italic text-slate-500" data-testid="adminv3-pricing-update-disabled-hint">
            {labels.submitDisabledHint}
          </span>
        )}
        <button
          type="button"
          onClick={handleOpenModal}
          disabled={submitDisabled}
          className={cn(
            'inline-flex items-center gap-2 rounded-xl px-4 py-2 text-[13px] font-semibold text-white transition',
            submitDisabled ? 'cursor-not-allowed bg-slate-300' : 'bg-emerald-600 hover:bg-emerald-700',
          )}
          data-testid="adminv3-pricing-open-modal"
        >
          {labels.submit}
        </button>
      </div>

      {/* Validation summary banner (when errors exist) */}
      {Object.keys(validationErrors).length > 0 && (
        <div
          className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-[12.5px] text-rose-800"
          data-testid="adminv3-pricing-validation-banner"
        >
          <div className="font-semibold">{labels.validationFailed}</div>
          <ul className="mt-1.5 list-inside list-disc space-y-0.5">
            {Object.entries(validationErrors).map(([path, msg]) => (
              <li key={path} className="font-mono text-[11.5px]">
                <span className="text-rose-600">{path}</span>: <span className="not-italic">{msg}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Confirmation modal */}
      <ConfirmationModal
        open={modalOpen}
        diff={diffEntries}
        submitting={mutation.loading}
        errorMessage={modalError}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default PricingConfigPage;
